<script lang="ts">
  let { options, selected = $bindable([]), label = '' }: {
    options: string[];
    selected: string[];
    label?: string;
  } = $props();

  let open = $state(false);
  let container: HTMLDivElement;
  let fieldEl: HTMLDivElement;

  /**
   * Position the menu against the field in VIEWPORT coordinates.
   *
   * The menu MUST stay `position: fixed`. Today wraps these in `.draft-card` and
   * `.ctask-swipe-wrap`, both `overflow: hidden`, which clip an absolutely positioned
   * menu to nothing near a card's bottom edge — the picker then looks dead. Fixed
   * escapes the clip, at the cost of placing it by hand.
   *
   * Flips above the field when there isn't room below, which is exactly the
   * bottom-of-card case that surfaced this.
   */
  function anchor(node: HTMLElement) {
    const place = () => {
      if (!fieldEl) return;
      const r = fieldEl.getBoundingClientRect();
      const h = node.offsetHeight;
      const below = window.innerHeight - r.bottom;
      node.style.width = `${r.width}px`;
      node.style.left = `${r.left}px`;
      // 2px gap either side.
      node.style.top = below < h + 8 && r.top > h + 8
        ? `${r.top - h - 2}px`
        : `${r.bottom + 2}px`;
    };
    place();
    // capture:true — the page scroller isn't `window`, so a bubbling listener
    // would never see the scroll that moves the field.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return {
      destroy() {
        window.removeEventListener('scroll', place, true);
        window.removeEventListener('resize', place);
      },
    };
  }

  function toggle(item: string) {
    if (selected.includes(item)) {
      selected = selected.filter(x => x !== item);
    } else {
      selected = [...selected, item];
    }
  }

  function remove(item: string) {
    selected = selected.filter(x => x !== item);
  }

  function handleClickOutside(e: MouseEvent) {
    if (container && !container.contains(e.target as Node)) {
      open = false;
    }
  }

  let unselected = $derived(options.filter(o => !selected.includes(o)));
</script>

<svelte:window onclick={handleClickOutside} />

<div class="ms-container" bind:this={container}>
  {#if label}
    <label class="text-xs text-dim">{label}</label>
  {/if}
  <div
    class="dd-field"
    bind:this={fieldEl}
    role="combobox"
    tabindex="0"
    aria-expanded={open}
    onclick={() => open = !open}
    onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && (open = !open)}
  >
    {#if selected.length === 0}
      <span class="dd-placeholder">Select...</span>
    {:else}
      {#each selected as item}
        <span class="ms-tag">
          {item}
          <span
            class="ms-tag-x"
            role="button"
            tabindex="0"
            onclick={(e) => { e.stopPropagation(); remove(item); }}
            onkeydown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); remove(item); } }}
          >&times;</span>
        </span>
      {/each}
    {/if}
    <span class="dd-arrow" class:open></span>
  </div>
  <!-- Opens even with nothing left to offer. Do NOT gate this on `unselected.length > 0`:
       when every option is already selected the arrow would flip ▼→▲ with nothing
       appearing, and the control reads as broken. Today hits that constantly — it pre-fills
       TO from the draft's recipient (`Today.svelte:199`), so a client with one contact has
       an empty list every time. An empty menu with a muted row is feedback. -->
  {#if open}
    <div class="dd-menu" use:anchor>
      {#each unselected as item}
        <div
          class="dd-option"
          role="option"
          tabindex="0"
          aria-selected={false}
          onclick={(e) => { e.stopPropagation(); toggle(item); }}
          onkeydown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); toggle(item); } }}
        >{item}</div>
      {/each}
      {#if unselected.length === 0}
        <div class="dd-empty">
          {options.length === 0 ? 'No contacts found' : 'All of them are already added'}
        </div>
      {/if}
    </div>
  {/if}
</div>

<style>
  /* The field, arrow, menu and option styling are `.dd-*` in app.css — this component is
     the BENCHMARK every other dropdown is matched against, so the values live somewhere
     both can reach. Change them there; only the chips below are this component's own.
     Note the focus ring is accent-keyed (`.dd-field:focus-visible`) — don't reintroduce a
     hardcoded blue glow, it reads wrong in the coral themes. */
  .ms-container {
    position: relative;
  }
  .ms-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 6px;
    background: var(--accent);
    color: #fff;
    border-radius: 3px;
    font-size: 11px;
    line-height: 1.4;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .ms-tag-x {
    cursor: pointer;
    font-size: 13px;
    line-height: 1;
    opacity: 0.8;
  }
  .ms-tag-x:hover {
    opacity: 1;
  }
</style>
